import { useEffect, useRef, useState } from 'react';

import type { App } from '@keboola/api-client/developerPortal/types';
import { Alert, Button, FileUpload, Modal, toast } from '@keboola/design';

import { getApiErrorMessage } from '../../auth/errors';
import { useUploadAppIcon } from '../../vendors/queries';

import { checkIconFile, iconRejectionMessage, REQUIRED_ICON_SIZE } from './icon';

type Chosen = { file: File; previewUrl: string };

/**
 * Replaces a component's icon: pick a PNG, see it, upload it.
 *
 * - Upload goes apps-api presigned link, then straight to S3 (see `uploadIcon`).
 * - The size check (`checkIconFile`) is the only one: S3 accepts any size.
 */
export function IconUploadModal({ app }: { app: App }) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useUploadAppIcon(app.vendor.id);
  // In refs: a blob URL outlives React state, and a decode can finish after its pick was replaced.
  const previewUrlRef = useRef<string | null>(null);
  const currentPickRef = useRef(0);

  // The only unconditional revoke; everything else goes through `holdPreview`.
  useEffect(
    () => () => {
      if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  /** Takes ownership of one preview URL at a time, revoking whichever it replaces. */
  const holdPreview = (next: Chosen | null) => {
    if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = next?.previewUrl ?? null;
    setChosen(next);
  };

  const clearChoice = () => {
    currentPickRef.current += 1;
    holdPreview(null);
  };

  const close = () => {
    setOpen(false);
    clearChoice();
    setError(null);
  };

  const choose = async (files: File[]) => {
    const file = files[0];
    if (file === undefined) return;

    clearChoice();
    const pick = currentPickRef.current;
    const result = await checkIconFile(file);
    // Superseded while decoding: revoke here, nothing else knows this URL.
    if (pick !== currentPickRef.current) {
      if (result.ok) URL.revokeObjectURL(result.previewUrl);
      return;
    }
    if (!result.ok) {
      setError(iconRejectionMessage(result));
      return;
    }
    setError(null);
    holdPreview({ file, previewUrl: result.previewUrl });
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else close();
      }}
    >
      <Modal.Trigger asChild>
        <Button variant="secondary" size="small">
          Change icon
        </Button>
      </Modal.Trigger>
      <Modal.Content size="medium">
        <Modal.Header
          description={
            <>
              A square PNG, exactly {REQUIRED_ICON_SIZE}×{REQUIRED_ICON_SIZE} px. See the{' '}
              <a
                href="https://developers.keboola.com/extend/publish/#component-icon"
                target="_blank"
                rel="noopener noreferrer"
                // Underlined: WCAG 1.4.1 / axe `link-in-text-block`.
                className="tw:text-primary tw:underline"
              >
                icon guidelines
              </a>
              .
            </>
          }
        >
          Change component icon
        </Modal.Header>
        <Modal.Body>
          {error !== null && (
            <Alert variant="error" title="That file cannot be used" className="tw:mb-4">
              {error}
            </Alert>
          )}

          {chosen === null ? (
            <FileUpload
              accept={{ 'image/png': ['.png'] }}
              maxFiles={1}
              multiple={false}
              onFilesChange={(files) => void choose(files)}
              // A rejected file never reaches `checkIconFile`; name the real reason (e.g.
              // `too-many-files`).
              onFilesRejected={(rejections) =>
                setError(
                  iconRejectionMessage({
                    reason: rejections.some((rejection) =>
                      rejection.errors.some(({ code }) => code === 'too-many-files'),
                    )
                      ? 'too-many'
                      : 'not-png',
                  }),
                )
              }
            >
              <FileUpload.DropZone />
            </FileUpload>
          ) : (
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-4">
              {/* Muted panel: most icons are transparent PNGs. */}
              <div className="tw:rounded-md tw:bg-muted tw:p-4">
                <img
                  src={chosen.previewUrl}
                  alt="New component icon"
                  width={REQUIRED_ICON_SIZE / 2}
                  height={REQUIRED_ICON_SIZE / 2}
                />
              </div>
              <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">{chosen.file.name}</p>
              <Button variant="invisible" size="small" onClick={clearChoice}>
                Choose a different file
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Modal.Close>
          <Button
            variant="primary"
            // `isLoading` does not disable; without `disabled` every click re-sends the PUT.
            disabled={chosen === null || upload.isPending}
            isLoading={upload.isPending}
            onClick={async () => {
              if (chosen === null) return;
              try {
                await upload.mutateAsync({ appId: app.id, png: chosen.file });
                toast.success('The icon has been updated. It may take a few minutes to appear.');
                close();
              } catch (uploadError) {
                setError(getApiErrorMessage(uploadError, 'Could not upload the icon.'));
              }
            }}
          >
            Upload icon
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
